import { lazy, Suspense, Component, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useCartSync } from "@/hooks/useCartSync";
import Index from "./pages/Index.tsx";

const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const NotreHistoire = lazy(() => import("./pages/NotreHistoire.tsx"));
const Install = lazy(() => import("./pages/Install.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const CommandePersonnalisee = lazy(() => import("./pages/CommandePersonnalisee.tsx"));
const Merci = lazy(() => import("./pages/Merci.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex justify-center items-center min-h-screen bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background p-6 text-center">
          <h1 className="text-2xl font-display font-bold mb-2">Oups, une erreur est survenue</h1>
          <p className="text-muted-foreground mb-4">Veuillez rafraîchir la page.</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">
            Rafraîchir
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const CartSyncWrapper = ({ children }: { children: ReactNode }) => {
  useCartSync();
  return <>{children}</>;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <CartSyncWrapper>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/product/:handle" element={<Suspense fallback={<PageLoader />}><ProductDetail /></Suspense>} />
              <Route path="/notre-histoire" element={<Suspense fallback={<PageLoader />}><NotreHistoire /></Suspense>} />
              <Route path="/install" element={<Suspense fallback={<PageLoader />}><Install /></Suspense>} />
              <Route path="/contact" element={<Suspense fallback={<PageLoader />}><Contact /></Suspense>} />
              <Route path="/commande-personnalisee" element={<Suspense fallback={<PageLoader />}><CommandePersonnalisee /></Suspense>} />
              <Route path="/merci" element={<Suspense fallback={<PageLoader />}><Merci /></Suspense>} />
              <Route path="/unsubscribe" element={<Suspense fallback={<PageLoader />}><Unsubscribe /></Suspense>} />
              <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
            </Routes>
          </CartSyncWrapper>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
